// Sources view: the documents behind the tree, and what each one is evidence for.
//
// A seventh LAYOUT rather than a seventh chip on the toolbar. The toolbar row
// already carries search, layout, seat, filter, share and Add person - about
// its whole width once the assistant panel is open - and "the documents" is
// genuinely another way of reading the same tree, which is what the layout
// switcher is for.
//
// It is also the only surface that shows the OCR text, which is why this view
// fetches its own payload instead of reading ctx.nodes: a few dozen
// transcriptions is a quarter of a megabyte, and the tree payload is re-read on
// a timer.
const KIND_ICONS = {
    bidprentje: "bi-card-text",
    birth_record: "bi-file-earmark-text",
    marriage_record: "bi-file-earmark-text",
    death_record: "bi-file-earmark-text",
    gravestone: "bi-signpost",
    census: "bi-table",
    photo: "bi-image",
    user_tree: "bi-diagram-3",
    other: "bi-paperclip"
}

export function renderSources(ctx) {
    let wrap = document.createElement("div")
    wrap.className = "ft-sources"
    let state = { query: "", sources: null, error: null }

    let filters = document.createElement("div")
    filters.className = "ft-sources-filters"
    let search = document.createElement("input")
    search.type = "search"
    search.className = "form-control form-control-sm ft-sources-search"
    // Names and dates inside a scanned card are invisible to every other
    // search in the app - that is most of why the transcript exists - so this
    // box reads the transcripts too, not just the titles.
    search.placeholder = "Search documents and transcripts"
    search.setAttribute("aria-label", "Search documents and transcripts")
    search.addEventListener("input", () => { state.query = search.value.trim().toLowerCase(); paint() })
    filters.appendChild(search)
    if (!ctx.publicView && !ctx.readOnly && ctx.sourcesUrl) {
        let add = document.createElement("button")
        add.type = "button"
        add.className = "btn btn-sm btn-outline-primary ft-sources-add"
        add.innerHTML = '<i class="bi bi-plus-lg" aria-hidden="true"></i> Add document'
        add.addEventListener("click", () => {
            document.dispatchEvent(new CustomEvent("family-tree:add-source", { detail: {} }))
        })
        filters.appendChild(add)
    }
    if (ctx.filterSlot) ctx.filterSlot.appendChild(filters)
    else wrap.appendChild(filters)

    let body = document.createElement("div")
    body.className = "ft-sources-body"
    body.textContent = "Loading…"
    wrap.appendChild(body)

    function paint() {
        body.innerHTML = ""
        if (state.error) {
            body.appendChild(note(state.error))
            return
        }
        if (!state.sources) {
            body.textContent = "Loading…"
            return
        }
        if (!state.sources.length) {
            body.appendChild(empty(ctx))
            return
        }
        let matches = state.sources.filter((source) => matchesQuery(source, state.query))
        if (!matches.length) {
            body.appendChild(note("No document matches that."))
            return
        }
        matches.forEach((source) => body.appendChild(card(source, ctx)))
    }

    if (ctx.sourcesUrl) {
        fetch(ctx.sourcesUrl, { credentials: "same-origin", headers: { Accept: "application/json" } })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
            .then((data) => { state.sources = data.sources || []; paint() })
            .catch(() => { state.error = "Could not load the documents."; paint() })
    } else {
        state.sources = []
        paint()
    }

    // A document list is a document, not a canvas: it scrolls, it does not pan.
    return { element: wrap, panZoom: false }
}

function matchesQuery(source, query) {
    if (!query) return true
    let haystack = [source.title, source.repository, source.reference, source.kind_label, source.transcript]
        .concat((source.cites || []).map((c) => `${c.name} ${c.claim || ""}`))
        .join(" ")
        .toLowerCase()
    return haystack.includes(query)
}

function card(source, ctx) {
    let el = document.createElement("article")
    el.className = "ft-source"
    el.dataset.sourceId = source.id

    if (source.image && source.document_url) {
        let thumb = document.createElement("a")
        thumb.className = "ft-source-thumb"
        thumb.href = source.document_url
        thumb.target = "_blank"
        thumb.rel = "noopener"
        let img = document.createElement("img")
        img.src = source.document_url
        img.alt = source.title
        img.loading = "lazy"
        thumb.appendChild(img)
        el.appendChild(thumb)
    }

    let main = document.createElement("div")
    main.className = "ft-source-main"

    let head = document.createElement("div")
    head.className = "ft-source-head"
    let icon = document.createElement("i")
    icon.className = `bi ${KIND_ICONS[source.kind] || KIND_ICONS.other} ft-source-icon`
    icon.setAttribute("aria-hidden", "true")
    let title = document.createElement("h3")
    title.className = "ft-source-title"
    title.textContent = source.title
    head.append(icon, title)
    // A scanned register and somebody's online tree are not the same evidence,
    // and the badge is where that is said in one word.
    let kind = document.createElement("span")
    // Two facts, and they are different questions: WHAT the document is, and
    // whether it is the original recording or a copy of one. A memorial card is
    // a real document and still derivative; a probate files as "archival
    // record" and is as original as anything in a tree.
    kind.className = `ft-source-kind${source.standing === "original" ? "" : " ft-source-kind--reported"}`
    kind.textContent = source.kind_label
    kind.title = source.standing_label || ""
    head.appendChild(kind)
    main.appendChild(head)

    let line = [source.citation_line, source.accessed_on ? `accessed ${source.accessed_on}` : null].filter(Boolean).join(" · ")
    if (line) main.appendChild(meta(line))

    if (source.url) {
        let link = document.createElement("a")
        link.className = "ft-source-link"
        link.href = source.url
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        link.textContent = source.url
        main.appendChild(link)
    }
    if (!source.image && source.document_url) {
        let file = document.createElement("a")
        file.className = "ft-source-link"
        file.href = source.document_url
        file.target = "_blank"
        file.rel = "noopener"
        file.textContent = source.filename || "Open the file"
        main.appendChild(file)
    }
    // What is shown above is a converted reading copy, because no browser
    // paints a TIFF. The scan itself is the record, so it stays one press away.
    if (source.original_url) {
        let original = document.createElement("a")
        original.className = "ft-source-link"
        original.href = source.original_url
        original.target = "_blank"
        original.rel = "noopener"
        original.textContent = `Download ${source.filename || "the original scan"}`
        main.appendChild(original)
    }

    let cites = source.cites || []
    let citeRow = document.createElement("div")
    citeRow.className = "ft-source-cites"
    if (cites.length) {
        let label = document.createElement("span")
        label.className = "ft-source-cites-label"
        label.textContent = "Cited for"
        citeRow.appendChild(label)
        cites.forEach((cite) => citeRow.appendChild(citeChip(cite, ctx)))
    } else {
        // The honest empty state: a filed document nothing rests on is a
        // document nobody can find their way to from a person.
        let hint = document.createElement("span")
        hint.className = "ft-source-cites-empty"
        hint.textContent = "Not cited for anything yet"
        citeRow.appendChild(hint)
    }
    main.appendChild(citeRow)

    // The quotes, under the chips rather than inside them. What a document
    // SAYS about a person is the most valuable thing on this card and the
    // longest: some of these notes run to a paragraph of reasoning about why
    // one of two identically-named men is the right one. A chip cannot hold
    // that - it is a pill built for a name, and a pill stretched around an
    // essay is what made this list unreadable.
    let quotes = cites.filter((cite) => cite.claim)
    if (quotes.length) {
        let list = document.createElement("div")
        list.className = "ft-source-claims"
        quotes.forEach((cite) => list.appendChild(claimBlock(cite)))
        main.appendChild(list)
    }

    if (source.transcript) main.appendChild(transcript(source))
    else if (source.transcript_status === "pending") main.appendChild(meta("Reading the document…"))
    else if (source.transcript_status === "failed") main.appendChild(meta("Couldn't read this one - the image is still here."))
    else if (source.transcript_status === "unfunded") main.appendChild(meta("Not read: reading a scan takes 2 credits and your balance doesn't cover it. The image is filed."))

    // A document filed from chat has no image - that is the normal way one gets
    // recorded, while somebody is reading an archive - so the scan arrives
    // later and needs somewhere to land that is not "delete and re-file".
    // Correcting the record, not the picture. A citation line is what every
    // reader of the tree is asked to trust, and until now a reference typed
    // from memory or an act number misread off a scan could only be fixed by
    // deleting the document - which takes every citation on it too.
    if (!ctx.publicView && !ctx.readOnly && ctx.sourcesUrl) {
        let edit = document.createElement("button")
        edit.type = "button"
        edit.className = "ft-source-edit"
        edit.textContent = "Edit details"
        edit.addEventListener("click", () => {
            // The record itself travels with the event: this view has its own
            // copy of the listing, and the controller's catalog is only loaded
            // when a PERSON's sources are opened - so looking it up there
            // silently did nothing from here.
            document.dispatchEvent(new CustomEvent("family-tree:edit-source", { detail: { source } }))
        })
        main.appendChild(edit)
    }

    if (!ctx.publicView && !ctx.readOnly && ctx.sourcesUrl) {
        let add = document.createElement("label")
        add.className = "ft-source-scan"
        add.textContent = source.document_url ? "Replace the scan" : "Add the scan"
        let input = document.createElement("input")
        input.type = "file"
        input.accept = "image/*,application/pdf"
        input.hidden = true
        input.addEventListener("change", () => {
            if (!input.files[0]) return

            add.textContent = "Uploading…"
            document.dispatchEvent(new CustomEvent("family-tree:upload-scan", {
                detail: { sourceId: source.id, file: input.files[0] }
            }))
        })
        add.appendChild(input)
        main.appendChild(add)
    }

    if (!ctx.publicView && !ctx.readOnly && source.deletable) {
        let remove = document.createElement("button")
        remove.type = "button"
        remove.className = "ft-source-remove"
        remove.textContent = "Remove document"
        remove.addEventListener("click", () => {
            document.dispatchEvent(new CustomEvent("family-tree:remove-source", { detail: { sourceId: source.id, title: source.title } }))
        })
        main.appendChild(remove)
    }

    el.appendChild(main)
    return el
}

// A name that takes you to the person, plus what the document says about them
// when that was worth writing down - which is also how a disagreement between
// two documents reads here rather than only on the person.
function citeChip(cite, ctx) {
    let chip = document.createElement(cite.person_id ? "button" : "span")
    chip.className = "ft-source-cite"
    chip.textContent = citeLabel(cite)
    if (cite.person_id) {
        chip.type = "button"
        chip.addEventListener("click", () => {
            document.dispatchEvent(new CustomEvent("family-tree:focus-person", { detail: { personId: cite.person_id } }))
        })
    }
    return chip
}

function citeLabel(cite) {
    return cite.fact && cite.fact !== "person" && cite.fact !== "link" ? `${cite.name} · ${cite.fact_label}` : cite.name
}

// One quote, attributed and clamped. Three lines is enough to see what the
// document says and whether to read on; the rest is one click away. Without
// the clamp a single card can run longer than the whole rest of the list, and
// the reader loses the thread of which document they are even looking at.
const CLAIM_CLAMP_LINES = 3

function claimBlock(cite) {
    let block = document.createElement("div")
    block.className = "ft-source-claim"

    let who = document.createElement("span")
    who.className = "ft-source-claim-who"
    who.textContent = citeLabel(cite)

    let text = document.createElement("p")
    text.className = "ft-source-claim-text"
    text.textContent = cite.claim
    block.append(who, text)

    // The toggle is added only when it has something to reveal, which cannot
    // be known from the string length - it depends on the rendered width. So
    // measure after layout and leave short quotes alone entirely.
    requestAnimationFrame(() => {
        if (text.scrollHeight <= text.clientHeight + 1) return

        let more = document.createElement("button")
        more.type = "button"
        more.className = "ft-source-claim-more"
        more.textContent = "Show more"
        more.addEventListener("click", () => {
            let open = block.classList.toggle("ft-source-claim--open")
            more.textContent = open ? "Show less" : "Show more"
        })
        block.appendChild(more)
    })
    return block
}

// Collapsed by default: a transcription is half a page of dense Dutch, and
// twenty of them open at once is not a list any more.
function transcript(source) {
    let details = document.createElement("details")
    details.className = "ft-source-transcript"
    let summary = document.createElement("summary")
    summary.textContent = "Transcription"
    let pre = document.createElement("pre")
    pre.textContent = source.transcript
    details.append(summary, pre)
    return details
}

function meta(text) {
    let el = document.createElement("p")
    el.className = "ft-source-meta"
    el.textContent = text
    return el
}

function note(text) {
    let el = document.createElement("p")
    el.className = "ft-sources-note"
    el.textContent = text
    return el
}

function empty(ctx) {
    let el = document.createElement("div")
    el.className = "ft-sources-empty"
    let title = document.createElement("h3")
    title.textContent = "No documents yet"
    let hint = document.createElement("p")
    hint.textContent = ctx.publicView
        ? "Nothing in this tree has a document behind it yet."
        : "Memorial cards, certificates, gravestone photos, a page from someone else's tree - add one and cite it on the people it names."
    el.append(title, hint)
    return el
}
